import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/AuthContext";
import {
  getCheckoutCampaignsCache,
  getCampaignCheckoutUrls,
  saveCampaignCheckoutUrl,
  deleteCampaignCheckoutUrl,
  type Campaign,
  type CampaignCheckoutUrl,
} from "api/order-entry.api";

interface CampaignRow {
  id: number;
  campaignId: number;
  campaignName: string;
  checkoutUrl: string | null;
  updatedAt: string | null;
}

export default function CheckoutUrlsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super-admin";
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<CampaignRow | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<CampaignRow | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ["checkout-campaigns-cache"],
    queryFn: () => getCheckoutCampaignsCache().then((r) => r.data),
    enabled: isAdmin,
  });

  const { data: urlsData, isLoading: urlsLoading } = useQuery({
    queryKey: ["campaign-checkout-urls"],
    queryFn: () => getCampaignCheckoutUrls().then((r) => r.data),
    enabled: isAdmin,
  });

  const allRows: CampaignRow[] = useMemo(() => {
    const urlByCampaignId = new Map<number, CampaignCheckoutUrl>(
      (urlsData?.urls ?? []).map((u: CampaignCheckoutUrl) => [u.campaignId, u])
    );
    return (campaignsData?.campaigns ?? []).map((c: Campaign) => {
      const saved = urlByCampaignId.get(c.campaignId);
      return {
        id: c.campaignId,
        campaignId: c.campaignId,
        campaignName: c.campaignName,
        checkoutUrl: saved?.checkoutUrl ?? null,
        updatedAt: saved?.updatedAt ?? null,
      };
    });
  }, [campaignsData, urlsData]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r: CampaignRow) =>
        r.campaignName.toLowerCase().includes(q) ||
        String(r.campaignId).includes(q) ||
        (r.checkoutUrl ?? "").toLowerCase().includes(q)
    );
  }, [allRows, search]);

  const savedCount = allRows.filter((r: CampaignRow) => r.checkoutUrl).length;

  const saveMutation = useMutation({
    mutationFn: () =>
      saveCampaignCheckoutUrl(editing!.campaignId, checkoutUrl.trim()),
    onSuccess: (res) => {
      setSnackbar({
        open: true,
        message: res.data.message || "Checkout URL saved",
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["campaign-checkout-urls"] });
      setEditing(null);
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save checkout URL",
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (campaignId: number) => deleteCampaignCheckoutUrl(campaignId),
    onSuccess: () => {
      setSnackbar({ open: true, message: "Checkout URL removed", severity: "success" });
      queryClient.invalidateQueries({ queryKey: ["campaign-checkout-urls"] });
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to remove checkout URL",
        severity: "error",
      });
    },
  });

  if (!isAdmin) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Typography
              component="span"
              sx={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() => navigate("/")}
            >
              Go back
            </Typography>
          }
        >
          You don't have access to this page.
        </Alert>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    { field: "campaignId", headerName: "ID", width: 90 },
    {
      field: "campaignName",
      headerName: "Campaign",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "checkoutUrl",
      headerName: "Checkout URL",
      flex: 1.6,
      minWidth: 300,
      renderCell: (params) =>
        params.value ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }} />
            <Tooltip title={params.value}>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0, fontFamily: "monospace", fontSize: "0.8rem" }}
              >
                {params.value}
              </Typography>
            </Tooltip>
            <IconButton
              size="small"
              component="a"
              href={params.value}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Stack>
        ) : (
          <Chip label="Not set" size="small" variant="outlined" sx={{ color: "text.secondary" }} />
        ),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value ? dayjs(params.value).format("MMM D, YYYY h:mm A") : "-"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      align: "right",
      renderCell: (params) => {
        const row = params.row as CampaignRow;
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={row.checkoutUrl ? "Edit checkout URL" : "Set checkout URL"}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(row);
                  setCheckoutUrl(row.checkoutUrl ?? "");
                }}
                sx={{
                  bgcolor: row.checkoutUrl ? "transparent" : "primary.main",
                  color: row.checkoutUrl ? "primary.main" : "#fff",
                  "&:hover": {
                    bgcolor: row.checkoutUrl ? "action.hover" : "primary.dark",
                  },
                }}
              >
                {row.checkoutUrl ? (
                  <EditIcon fontSize="small" />
                ) : (
                  <LinkIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {row.checkoutUrl && (
              <Tooltip title="Remove checkout URL">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const handleSave = () => {
    if (!checkoutUrl.trim()) {
      setSnackbar({ open: true, message: "Checkout URL is required", severity: "error" });
      return;
    }
    try {
      // eslint-disable-next-line no-new
      new URL(checkoutUrl.trim());
    } catch {
      setSnackbar({ open: true, message: "Enter a valid URL", severity: "error" });
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>
            Checkout URLs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Save a known-working base checkout URL per campaign (e.g. copied from a
            real Shopify checkout redirect). Order Entry uses it instead of guessing
            the store domain/path, so "Complete your purchase" links reliably show
            the right product. Campaigns without one fall back to the default pattern.
          </Typography>
        </Box>
        <Chip
          label={`${savedCount} / ${allRows.length} campaigns configured`}
          color={savedCount === allRows.length && allRows.length > 0 ? "success" : "default"}
          variant="outlined"
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
      </Stack>

      <TextField
        size="small"
        label="Search campaigns or URLs"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 280, mb: 2 }}
      />

      <DataGrid
        rows={rows}
        columns={columns}
        loading={campaignsLoading || urlsLoading}
        disableRowSelectionOnClick
        autoHeight
        rowHeight={60}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "background.paper",
            fontWeight: 700,
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
        }}
      />

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Checkout URL — {editing?.campaignName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Paste the base checkout URL for this campaign's store (everything before
            the <code>?products=</code> query string).
          </Typography>
          <TextField
            label="Checkout URL"
            value={checkoutUrl}
            onChange={(e) => setCheckoutUrl(e.target.value)}
            fullWidth
            size="small"
            placeholder="https://go.example.com/checkout"
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            startIcon={
              saveMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {saveMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove checkout URL?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the saved checkout URL for{" "}
            <strong>{deleteTarget?.campaignName}</strong>. Order Entry will fall back
            to the default URL pattern for this campaign.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate(deleteTarget!.campaignId)}
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {deleteMutation.isPending ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
